// open-close — interaction primitive.
//
// Builds the lid-style open/close behavior shared by box-family fixtures
// (longbox, shortbox today; any future kind with an authored Lid node).
//
// Three concerns wired by this primitive:
//
//   1. Lid pivot setup — creates a THREE.Group at the chosen hinge
//      edge, reparents the lid mesh into it preserving world TRS.
//      Stage's `applyLidAngle()` rotates the pivot around its local X;
//      the lid's non-hinge edge swings up.
//
//   2. Hit zone — an invisible BoxGeometry sized to the box footprint +
//      headroom, registered with the interactable-dispatcher so E-key
//      on the lid (or click) opens it.
//
//   3. State plumbing — writes lidPivotNode / lidBaseQuat /
//      lidPivotBaseQuat / lidHitZone to the composedEntry so stage.js's
//      existing lid-angle animation reads them.
//
// Manifest contract (manifest.interactions[N] where kind="open-close"):
//   - target               (string)  — must be "lidNode" (canonical mesh ref)
//   - trigger              (string)  — "E" or "click" (today both fire on E)
//   - hinge.edge           (string?) — "back" | "left" | "right" — the CATALOGUE's
//                                      default for this interaction. The authored
//                                      per-anchor edge outranks it: the resolution
//                                      order is scene.template.knobs.boxOrientations
//                                      [anchor].hinge > anchor.extras.hinge > this
//                                      field > manifest.defaults.hinge > "back".
//                                      Same ladder facingDeg uses; see the block at
//                                      the read site for why it is not two ladders.
//   - hinge.openAngleRad   (number)  — stage.js's lid-animator reads this
//                                      from composedEntry.lidOpenAngle if set;
//                                      otherwise uses stage default.
//   - headRoom             (number)  — vertical clearance above box for hit zone (default 0.10m)
//
// Migration-window note: this primitive participates in stage.js's
// existing box state machine (boxState / activeBoxId / boxEntries Map).
// Phase 6+ refactoring can encapsulate the state machine per-fixture;
// for now the primitive does what longbox-anchor.js did, just in the
// engine namespace.

import * as THREE from "../../../../vendor/three.module.js";
import { registerInteraction } from "./index.js";

const HINGE_VALUES = new Set(["back", "left", "right"]);
function normalizeHinge(value) {
	const v = String(value || "").toLowerCase();
	return HINGE_VALUES.has(v) ? v : "back";
}

function wireOpenClose(ctx) {
	const { anchor, manifest, interactionDef, bodyMesh, lidMesh, slotResult, stage, scene, composedEntry } = ctx;
	if (!stage) {
		console.warn("[interaction:open-close] missing stage in context");
		return;
	}

	const laneCenter = slotResult?.extras?.laneCenter || anchor.position.clone();
	const laneSize   = slotResult?.extras?.laneSize   || new THREE.Vector3(0.12, 0.08, 0.08);
	const laneLength = laneSize.x;
	const laneHeight = laneSize.y;
	const laneDepth  = laneSize.z;

	// Front direction the composer supplies on composedEntry (matches
	// linear-spine-row's input); fall back to -X (facingDeg=180) so the
	// primitive degrades gracefully if absent.
	const frontDir = (composedEntry?.frontDir || new THREE.Vector3(-1, 0, 0)).clone().normalize();

	// Body bbox in world space — drives hinge location math.
	let longHalf  = laneLength * 0.5;
	let shortHalf = 0.12;
	let topY      = anchor.position.y + 0.21;
	if (bodyMesh) {
		bodyMesh.updateMatrixWorld(true);
		const bbox = new THREE.Box3().setFromObject(bodyMesh);
		longHalf  = (bbox.max.x - bbox.min.x) * 0.5;
		shortHalf = (bbox.max.z - bbox.min.z) * 0.5;
		topY      = bbox.max.y;
	}

	// ─────── Resolve hinge edge ───────
	//
	// 🔴 ASK-129 §E2E104 — THIS READ THE WRONG OBJECT, AND THE AUTHORED EDGE WAS NEVER APPLIED.
	//
	// The per-anchor knob map is on the SCENE (`scene.template.knobs.boxOrientations`) — that is where
	// the Studio writes it (`studio/index.js` → `onAnchorHingePick`), where GCG persists it through
	// `/anchors`, where Forge publishes it, and where the composer has always read its sibling
	// `facingDeg` from (`engine/composer.js:203`). This line read `manifest.knobs` — the FIXTURE
	// CATALOGUE's manifest, which is per-KIND and has no per-anchor map at all. Measured on the live
	// boot 2026-09-11: `scene.template.knobs.boxOrientations.LONGBOX_ANCHOR_02.hinge = "left"` is
	// published, and not one of the 29 catalogue manifests carries a `knobs` key. So the expression
	// was `undefined` for every anchor that has ever existed, the knob was WRITE-ONLY, and a merchant
	// who chose which edge her lid opens on got "back" regardless.
	//
	// Priority, and it now matches `facingDeg`'s exactly — the two are siblings in one map and must
	// not be resolved by two different ladders:
	//   scene per-anchor knob  >  the shell's authored extras  >  the interaction's own default  >
	//   the manifest's declared default  >  "back".
	const orient = scene?.template?.knobs?.boxOrientations?.[anchor.name] || {};
	const hinge = normalizeHinge(
		orient.hinge
		?? anchor.extras?.hinge
		?? interactionDef?.hinge?.edge
		?? manifest?.defaults?.hinge
		?? "back",
	);

	// Hit zone — invisible BoxGeometry sized to lane + head room.
	const headRoom = typeof interactionDef?.headRoom === "number" ? interactionDef.headRoom : 0.10;
	const hitW = Math.max(0.10, laneLength);
	const hitH = Math.max(0.05, laneHeight) + headRoom;
	const hitD = Math.max(0.05, laneDepth);
	const hitZone = new THREE.Mesh(
		new THREE.BoxGeometry(hitW, hitH, hitD),
		new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
	);
	hitZone.name = `lid_hit_zone_${anchor.name}`;
	hitZone.position.copy(laneCenter).add(new THREE.Vector3(0, hitH * 0.5, 0));
	// Hit zone's local X (BoxGeometry width = laneLength) aligns with frontDir.
	// Y-axis rotation that maps (1,0,0) → frontDir.
	const hitYaw = Math.atan2(-frontDir.z, frontDir.x);
	hitZone.quaternion.setFromEuler(new THREE.Euler(0, hitYaw, 0, "YXZ"));
	hitZone.userData.isLid = true;
	hitZone.userData.fixtureId = anchor.name;
	hitZone.userData.interactable = {
		kind: "longbox-lid",  // stage.js's interactable-dispatcher matches by this string
		fixtureKind: manifest?.kind || "longbox",
		fixtureIndex: anchor.index,
		fixtureId: anchor.name,
		onClick: ({ stage: s }) => {
			// Stage state machine semantics (preserved from legacy longbox-anchor.js):
			// - If any box is open elsewhere, force-close it + open this one
			// - If this box is closed, open it
			// - If this box is already open, the dispatcher routes to browse/hero
			//   per stage.js's existing internal logic
			if (s.boxEntries.size > 0 && anchor.name !== s.activeBoxId) {
				s.forceCloseActiveBox();
				s.activateBox(anchor.name);
				s.openLid();
			} else if (s.boxState === "closed") {
				s.openLid();
			}
		},
		describe: () => ({ kind: "longbox-lid", fixtureId: anchor.name }),
	};
	stage.stageRoot.add(hitZone);
	composedEntry.lidHitZone = hitZone;

	// Lid pivot — Group at hinge edge; reparent lid preserving world TRS.
	if (lidMesh) {
		const upWorld  = new THREE.Vector3(0, 1, 0);
		const leftSide = new THREE.Vector3().crossVectors(upWorld, frontDir).normalize();
		const topPos   = new THREE.Vector3(anchor.position.x, topY, anchor.position.z);
		let hingeCenter;
		let hingeAxis;
		if (hinge === "left") {
			hingeCenter = topPos.clone().addScaledVector(leftSide, +shortHalf);
			hingeAxis   = frontDir.clone();
		} else if (hinge === "right") {
			hingeCenter = topPos.clone().addScaledVector(leftSide, -shortHalf);
			hingeAxis   = frontDir.clone().negate();
		} else { // "back"
			hingeCenter = topPos.clone().addScaledVector(frontDir, -longHalf);
			hingeAxis   = leftSide.clone();
		}

		const lidPivot = new THREE.Group();
		lidPivot.name = `lid_pivot_${anchor.name}`;
		lidPivot.position.copy(hingeCenter);
		// Align pivot's local +X to the hinge axis so stage's
		// applyLidAngle() (which rotates around local X) lifts the
		// lid's non-hinge edge upward.
		lidPivot.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), hingeAxis);
		(stage._shellRoot || stage.stageRoot).add(lidPivot);
		lidMesh.updateMatrixWorld(true);
		lidPivot.attach(lidMesh);  // preserves lid's world transform

		composedEntry.lidNode         = lidMesh;
		composedEntry.lidPivotNode    = lidPivot;
		composedEntry.lidPivotBaseQuat = lidPivot.quaternion.clone();
		composedEntry.lidBaseQuat     = lidMesh.quaternion.clone();
	}

	// Optional lid-open-angle override for stage's animator to read.
	if (typeof interactionDef?.hinge?.openAngleRad === "number") {
		composedEntry.lidOpenAngle = interactionDef.hinge.openAngleRad;
	}

	// Dispose: remove hit zone + lid pivot from scene.
	return () => {
		if (composedEntry.lidHitZone?.parent) {
			composedEntry.lidHitZone.parent.remove(composedEntry.lidHitZone);
		}
		if (composedEntry.lidPivotNode?.parent) {
			composedEntry.lidPivotNode.parent.remove(composedEntry.lidPivotNode);
		}
	};
}

registerInteraction("open-close", wireOpenClose);

export { wireOpenClose };
